<script lang="ts">
  import { setupForm, errorsIf } from "./store";

  import { createEventDispatcher, onDestroy } from "svelte";
  import { writable } from "svelte/store";
  import debounce from "lodash.debounce";
  import { keyDown } from "../keyDown";

  import CloseIcon from "./Close.svg";

  const AUTOSAVE_DEBOUNCE = 5000;
  const AUTOSAVE_MAX_WAIT = 30000;
  /***
   * Form events:
   *
   * cancel -> When the form is canceled
   * beforesubmit -> before we start submit, detail = form data
   * submitted -> after *succesful* submit, detail = result
   * error -> error while submitting, detail = error
   * beforeautosave -> before we autosave (after debounce), detail = data to autosave
   * autosaved -> after *successful* autosave, detaul = result
   * autosaveerror -> error while autosaving, detail = error
   *
   **/

  // Props

  export let autosaveData: Record<string, any> = {};
  export let submit: (data: Record<string, any>) => Promise<void>;
  export let autosave: ((data: Record<string, any>) => Promise<void>) | undefined = undefined;
  export let submitButtonText = "Done";
  export let submittingText = "Saving...";
  // Two way binding for data, isDirty and isValid
  export let data: Record<string, any> = {};
  export let isValid = true;
  export let isDirty = false;
  export let caption: string | undefined = undefined;
  export let icon: string | undefined = undefined;
  export let rawData: Record<string, any> = data;
  export let noBackground = false;
  export let noMargins = false;

  // State
  let originalData = data; // Save it for canceling
  let submitPromise: Promise<void> | undefined;
  let isSubmitting = false;
  let canSubmit = false;
  let autosavePromise: Promise<void> | undefined;
  let autosaveDebounced = autosave ? debounce(onAutoSave, AUTOSAVE_DEBOUNCE, { maxWait: AUTOSAVE_MAX_WAIT }) : null;

  const isDisabled = writable(false);
  const dispatch = createEventDispatcher();

  if (!submit) {
    throw new Error("Must have a `submit` prop that will submit the form!");
  }

  export async function onAutoSave(data: Record<string, any>) {
    // skip if we don't have any autosave function
    if (!autosave) {
      return;
    }

    dispatch("beforeautosave", data);
    autosavePromise = autosave(data);
    try {
      const result = await autosavePromise;
      dispatch("autosaved", data);
    } catch (error) {
      dispatch("autosaveerror", error);
    }
  }

  export async function onSubmit() {
    // if submitting - no need to do anything
    if (isSubmitting) {
      return;
    }

    // Do nothing if we can't submit
    if (!canSubmit) {
      // TODO: Do a shake here, or maybe show errors?
      return;
    }

    // If nothing has changed, but data is valid, we cancel
    if (!$form.isDirty) {
      return onCancel();
    }

    // We can submit, so we:
    // 1. Disable any pending autosaves
    if (autosaveDebounced) {
      autosaveDebounced.cancel();
    }

    // 2. Disable the form:
    $isDisabled = true;

    // 3. create the submit promise
    try {
      // dispatch a before submit event
      dispatch("beforesubmit", $form.data);

      // Create a new submit promise
      submitPromise = submit($form.data);

      // Mark the form as submitting
      isSubmitting = true;
      // Wait for the submit to complete
      const result = await submitPromise;
      // mark submit as complited
      isSubmitting = false;
      // dispatch the submitted event with the results
      dispatch("submitted", result);
      // Un-disable the form
      $isDisabled = false;
    } catch (error) {
      console.error(error);
      // clear submitting status
      isSubmitting = false;
      // Handle error with the submit, un-disable the form + dispatch the error event
      $isDisabled = false;
      dispatch("error", error);
    }
  }

  export function onCancel() {
    // When canceling we need to:
    // 1. Disable any pending auto saves
    if (autosaveDebounced) {
      autosaveDebounced.cancel();
    }
    // 2. Disable the form
    $isDisabled = true;

    // 3. Reset external data to be original data, reset fields to original
    form.reset();

    dispatch("cancel");
  }

  // Create the form store
  const form = setupForm(data, autosaveData, onSubmit, onCancel, isDisabled);

  // Reactive valid / dirty props
  $: {
    isValid = $form.isValid;
    isDirty = $form.isDirty;
    canSubmit = $form.isValid && !$form.isPending;
  }

  // Reactive : update external binding when form is not pending is valid
  $: if (canSubmit) {
    data = $form.data;
  }

  $: rawData = $form.data;

  // Reactive autosave
  $: if ($form.isDirty && !$form.isPending && autosaveDebounced && !isSubmitting && !$isDisabled) {
    autosaveDebounced($form.data);
  }

  onDestroy(() => {
    if (autosaveDebounced) {
      autosaveDebounced.cancel();
      autosaveDebounced = null;
    }
  });
</script>

<form
  class="relative flex h-full flex-col items-start justify-between"
  class:bg-gray-50={!noBackground}
  on:submit|stopPropagation|preventDefault={onSubmit}
  use:keyDown={[
    { key: "Escape", handler: onCancel },
    { key: "Enter", handler: onSubmit, mod: "cmd" }
  ]}>
  <div class="h-full w-full">
    <slot name="overlay" {submitPromise}>
      {#await submitPromise}
        <div class="absolute inset-0 bg-transparent" />
      {:then}
        <div />
      {/await}
    </slot>

    <!-- form header -->
    <slot name="header" {onCancel} {caption} {icon}>
      {#if caption || icon}
        <div class="flex w-full items-center justify-between rounded rounded-b-none border border-l-0 border-r-0 border-t-0 bg-white p-4">
          <div class="flex items-center justify-start">
            {#if icon}
              <img class="mr-2 h-5" src={icon} alt={caption} />
            {/if}
            {caption}
          </div>
          <img class="cursor-pointer transition-opacity hover:opacity-75" src={CloseIcon} alt="Close" on:click={onCancel} />
        </div>
      {/if}
    </slot>

    <!-- form content here-->
    <div class="w-full" class:p-2={!noMargins}>
      <slot />
    </div>
  </div>

  <!-- footer content -->
  <slot name="footer" {onCancel} {onSubmit} errors={$form.errors} {isValid} {isSubmitting} {autosavePromise} {submitPromise} {canSubmit}>
    <div
      class="align-center border-px flex w-full items-center justify-between rounded-b border border-b-0 border-l-0 border-r-0 border-gray-200 py-2"
      class:bg-gray-50={!noBackground}
      class:px-2={!noMargins}
      class:pl-3={!noMargins}>
      <span
        on:click|preventDefault|stopPropagation={onCancel}
        class="clickable scale-in hover:text-danger-600 cursor-pointer text-13 text-nxgray-400 transition-colors">
        Cancel
      </span>

      <input
        type="submit"
        class="bg-success-600 rounded px-3 py-1 text-14 font-medium text-white shadow transition-colors"
        on:click|preventDefault|stopPropagation={onSubmit}
        class:clickable={canSubmit}
        class:hover:bg-success-500={canSubmit}
        class:bg-nxgray-200={isSubmitting || !canSubmit}
        class:cursor-not-allowed={isSubmitting || !canSubmit}
        class:text-gray-400={isSubmitting || !canSubmit}
        class:shadow-none={isSubmitting || !canSubmit}
        value={isSubmitting ? submittingText : submitButtonText} />
    </div>
  </slot>
</form>
